// Structure Tree View Component
// Displays AIAG-VDA hierarchical Process Item -> Process Step -> Work Element structure
// ============================================================

import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Folder, FileText, ListTree, Plus, Edit2, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { ProcessItem, ProcessStep, WorkElement } from '../types';

interface StructureTreeViewProps {
    processItems: ProcessItem[];
    processSteps: ProcessStep[];
    workElements: WorkElement[];
    isDark: boolean;
    onAddProcessItem?: () => void;
    onAddProcessStep?: (processItemId: string) => void;
    onAddWorkElement?: (processStepId: string) => void;
    onEditItem?: (type: 'item' | 'step' | 'element', id: string) => void;
    onDeleteItem?: (type: 'item' | 'step' | 'element', id: string) => void;
}

export const StructureTreeView: React.FC<StructureTreeViewProps> = ({
    processItems,
    processSteps,
    workElements,
    isDark,
    onAddProcessItem,
    onAddProcessStep,
    onAddWorkElement,
    onEditItem,
    onDeleteItem,
}) => {
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

    const toggleItem = (id: string) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedItems(newExpanded);
    };

    const toggleStep = (id: string) => {
        const newExpanded = new Set(expandedSteps);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedSteps(newExpanded);
    };

    // Organize hierarchy
    const sortedItems = [...processItems].sort((a, b) => a.order - b.order);

    return (
        <div className={clsx(
            'h-full overflow-auto p-4 rounded-lg border',
            isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
        )}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <ListTree size={20} className="text-purple-500" />
                    <h3 className={clsx('font-semibold', isDark ? 'text-white' : 'text-slate-800')}>
                        Structure Tree (AIAG-VDA)
                    </h3>
                </div>
                {onAddProcessItem && (
                    <button
                        onClick={onAddProcessItem}
                        className="px-3 py-1.5 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600 flex items-center gap-2"
                    >
                        <Plus size={14} />
                        Add Process Item
                    </button>
                )}
            </div>

            {/* Tree */}
            <div className="space-y-1">
                {sortedItems.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 text-sm">
                        <p>No process items yet.</p>
                        <p className="mt-1">Click "Add Process Item" to start building the structure tree.</p>
                    </div>
                ) : (
                    sortedItems.map(item => {
                        const isExpanded = expandedItems.has(item.id);
                        const itemSteps = processSteps.filter(step => step.parentProcessItemId === item.id);

                        return (
                            <div key={item.id} className="select-none">
                                {/* Process Item Level */}
                                <div
                                    className={clsx(
                                        'flex items-center gap-2 px-2 py-2 rounded cursor-pointer hover:bg-opacity-80 transition-colors',
                                        isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'
                                    )}
                                >
                                    <button
                                        onClick={() => toggleItem(item.id)}
                                        className="p-0.5 hover:bg-slate-600 rounded"
                                    >
                                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    </button>
                                    <Folder size={16} className="text-blue-500" />
                                    <span className={clsx('flex-1 font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                                        {item.name}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                        {itemSteps.length} step{itemSteps.length !== 1 ? 's' : ''}
                                    </span>
                                    <div className="flex gap-1">
                                        {onAddProcessStep && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onAddProcessStep(item.id);
                                                }}
                                                className="p-1 hover:bg-purple-500 hover:text-white rounded text-slate-400"
                                                title="Add Process Step"
                                            >
                                                <Plus size={14} />
                                            </button>
                                        )}
                                        {onEditItem && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onEditItem('item', item.id);
                                                }}
                                                className="p-1 hover:bg-blue-500 hover:text-white rounded text-slate-400"
                                                title="Edit"
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                        )}
                                        {onDeleteItem && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDeleteItem('item', item.id);
                                                }}
                                                className="p-1 hover:bg-red-500 hover:text-white rounded text-slate-400"
                                                title="Delete"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Process Steps under this Item */}
                                {isExpanded && (
                                    <div className="ml-6 space-y-1 mt-1">
                                        {itemSteps.map(step => {
                                            const isStepExpanded = expandedSteps.has(step.id);
                                            const stepElements = workElements.filter(el => el.processStepId === step.id);

                                            return (
                                                <div key={step.id}>
                                                    {/* Process Step Level */}
                                                    <div
                                                        className={clsx(
                                                            'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-opacity-80 transition-colors',
                                                            isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'
                                                        )}
                                                    >
                                                        <button
                                                            onClick={() => toggleStep(step.id)}
                                                            className="p-0.5 hover:bg-slate-600 rounded"
                                                        >
                                                            {isStepExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                        </button>
                                                        <FileText size={14} className="text-green-500" />
                                                        <span className={clsx('flex-1 text-sm', isDark ? 'text-slate-200' : 'text-slate-700')}>
                                                            {step.stepNumber}: {step.stepName}
                                                        </span>
                                                        <span className="text-xs text-slate-400">
                                                            {stepElements.length} element{stepElements.length !== 1 ? 's' : ''}
                                                        </span>
                                                        <div className="flex gap-1">
                                                            {onAddWorkElement && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onAddWorkElement(step.id);
                                                                    }}
                                                                    className="p-1 hover:bg-purple-500 hover:text-white rounded text-slate-400"
                                                                    title="Add Work Element"
                                                                >
                                                                    <Plus size={12} />
                                                                </button>
                                                            )}
                                                            {onEditItem && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onEditItem('step', step.id);
                                                                    }}
                                                                    className="p-1 hover:bg-blue-500 hover:text-white rounded text-slate-400"
                                                                    title="Edit"
                                                                >
                                                                    <Edit2 size={12} />
                                                                </button>
                                                            )}
                                                            {onDeleteItem && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onDeleteItem('step', step.id);
                                                                    }}
                                                                    className="p-1 hover:bg-red-500 hover:text-white rounded text-slate-400"
                                                                    title="Delete"
                                                                >
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Work Elements under this Step */}
                                                    {isStepExpanded && stepElements.length > 0 && (
                                                        <div className="ml-6 space-y-0.5 mt-0.5">
                                                            {stepElements.map(element => (
                                                                <div
                                                                    key={element.id}
                                                                    className={clsx(
                                                                        'flex items-center gap-2 px-2 py-1 rounded hover:bg-opacity-80 transition-colors',
                                                                        isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-100'
                                                                    )}
                                                                >
                                                                    <div className="w-4" /> {/* Spacer for alignment */}
                                                                    <div className="w-2 h-2 rounded-full bg-purple-400" />
                                                                    <span className={clsx('flex-1 text-xs', isDark ? 'text-slate-300' : 'text-slate-600')}>
                                                                        {element.name}
                                                                    </span>
                                                                    {element.duration && (
                                                                        <span className="text-xs text-slate-400">
                                                                            {element.duration}s
                                                                        </span>
                                                                    )}
                                                                    <div className="flex gap-1">
                                                                        {onEditItem && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    onEditItem('element', element.id);
                                                                                }}
                                                                                className="p-0.5 hover:bg-blue-500 hover:text-white rounded text-slate-400"
                                                                                title="Edit"
                                                                            >
                                                                                <Edit2 size={10} />
                                                                            </button>
                                                                        )}
                                                                        {onDeleteItem && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    onDeleteItem('element', element.id);
                                                                                }}
                                                                                className="p-0.5 hover:bg-red-500 hover:text-white rounded text-slate-400"
                                                                                title="Delete"
                                                                            >
                                                                                <Trash2 size={10} />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Legend */}
            <div className={clsx('mt-6 pt-4 border-t text-xs space-y-1', isDark ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500')}>
                <p className="font-medium mb-2">AIAG-VDA Structure Tree</p>
                <div className="flex items-center gap-2">
                    <Folder size={12} className="text-blue-500" />
                    <span>Process Item (highest level)</span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                    <FileText size={12} className="text-green-500" />
                    <span>Process Step (middle level)</span>
                </div>
                <div className="flex items-center gap-2 ml-8">
                    <div className="w-2 h-2 rounded-full bg-purple-400" />
                    <span>Work Element (lowest level)</span>
                </div>
            </div>
        </div>
    );
};

export default StructureTreeView;
